import {
  assertUploadWithin10Mb,
  uploadSizeLimitApiMessage,
} from "@/constants/uploadLimits";
import { getErrorMessage } from "@/utils/errorMessage";
import { ISiteActivationRepository } from "@/repositories/SiteActivationRepository";
import {
  ActivationChecklist,
  SiteReadinessData,
  DocumentReview,
  ActivationWorkflowStatus,
  CreateChecklistTemplateRequest,
  ChecklistTemplate,
  DashboardResponse,
  DashboardFilters,
  DashboardSummary,
  AuditHistoryResponse,
  AuditHistoryFilters,
  CreateActivationRequest,
  AddChecklistItemRequest,
  SiteActivationTask,
  AssignableUser,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskFilters,
  TasksResponse,
  StudyChecklistConfig,
  StudyChecklistConfigItem,
  WaivedGlobalItem,
  CreateStudyChecklistConfigRequest,
  AddStudyConfigItemRequest,
  WaiveGlobalItemRequest,
  ExcludeSiteItemRequest,
  AddSiteExceptionItemRequest,
  ChecklistItem,
} from "@/types/siteActivation/siteActivation.type";

export interface ISiteActivationUseCase {
  getDocumentDownloadUrl(
    studyId: string,
    siteId: string,
    id: string,
  ): Promise<{ url: string; fileName: string }>;
  // Checklist template operations
  createChecklistTemplate(
    data: CreateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate>;

  // Checklist operations
  getChecklistTemplate(
    studyId: string,
    siteId: string,
  ): Promise<ActivationChecklist>;
  updateChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: Partial<ActivationChecklist["items"][0]>,
  ): Promise<ActivationChecklist>;
  submitChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist>;

  // Activation creation and item management (simplified flow)
  createActivation(
    studyId: string,
    siteId: string,
    data: CreateActivationRequest,
  ): Promise<ActivationChecklist>;
  addChecklistItem(
    studyId: string,
    siteId: string,
    data: AddChecklistItemRequest,
  ): Promise<ActivationChecklist>;
  removeChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
  ): Promise<{ message: string }>;
  submitChecklistDraft(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist>;

  // Site readiness operations
  getSiteReadiness(studyId: string, siteId: string): Promise<SiteReadinessData>;

  // Document operations
  getDocumentReviews(
    studyId: string,
    siteId: string,
  ): Promise<DocumentReview[]>;
  uploadDocument(
    studyId: string,
    siteId: string,
    documentData: FormData,
  ): Promise<DocumentReview>;
  approveDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<DocumentReview>;
  rejectDocument(
    studyId: string,
    siteId: string,
    documentId: string,
    reason: string,
  ): Promise<DocumentReview>;
  deleteDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ message: string }>;

  // Workflow operations
  getActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationWorkflowStatus>;

  // PM-specific workflow operations
  startInitiation(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus>;
  approveChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus>;
  rejectChecklist(
    studyId: string,
    siteId: string,
    reason: string,
  ): Promise<ActivationWorkflowStatus>;

  // Dashboard operations
  getDashboard(filters?: DashboardFilters): Promise<DashboardResponse>;
  getDashboardSummary(studyId?: string): Promise<DashboardSummary>;

  // Export operations
  exportToCSV(filters?: DashboardFilters): Promise<Blob>;

  // Audit history
  getAuditHistory(filters?: AuditHistoryFilters): Promise<AuditHistoryResponse>;

  // Task operations
  getTasks(
    studyId: string,
    siteId: string,
    filters?: TaskFilters,
  ): Promise<TasksResponse>;
  getTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<SiteActivationTask>;
  createTask(
    studyId: string,
    siteId: string,
    data: CreateTaskRequest,
  ): Promise<SiteActivationTask>;
  updateTask(
    studyId: string,
    siteId: string,
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<SiteActivationTask>;
  deleteTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<{ message: string }>;
  getAssignableUsers(
    studyId: string,
    siteId: string,
  ): Promise<AssignableUser[]>;

  // Study Checklist Configuration operations
  getStudyChecklistConfig(studyId: string): Promise<StudyChecklistConfig>;
  createStudyChecklistConfig(
    studyId: string,
    data: CreateStudyChecklistConfigRequest,
  ): Promise<StudyChecklistConfig>;
  addStudyConfigItem(
    studyId: string,
    data: AddStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem>;
  waiveGlobalItem(
    studyId: string,
    data: WaiveGlobalItemRequest,
  ): Promise<WaivedGlobalItem>;

  // Site Item Exclusion and Exception operations
  excludeSiteItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: ExcludeSiteItemRequest,
  ): Promise<ChecklistItem>;
  addSiteExceptionItem(
    studyId: string,
    siteId: string,
    data: AddSiteExceptionItemRequest,
  ): Promise<ChecklistItem>;
}

export class SiteActivationUseCase implements ISiteActivationUseCase {
  constructor(private repository: ISiteActivationRepository) {}

  async createChecklistTemplate(
    data: CreateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate> {
    // Validation
    if (!data.name?.trim()) {
      throw new Error("Template name is required");
    }
    if (!data.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!data.items || data.items.length === 0) {
      throw new Error("At least one checklist item is required");
    }

    // Validate each item
    data.items.forEach((item, index) => {
      if (!item.title?.trim()) {
        throw new Error(`Item ${index + 1}: Title is required`);
      }
      if (!item.category?.trim()) {
        throw new Error(`Item ${index + 1}: Category is required`);
      }
    });

    return this.repository.createChecklistTemplate(data);
  }

  async getChecklistTemplate(
    studyId: string,
    siteId: string,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getChecklistTemplate(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch checklist template: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async updateChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: Partial<ActivationChecklist["items"][0]>,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId || !itemId) {
      throw new Error("Study ID, Site ID, and Item ID are required");
    }

    try {
      return await this.repository.updateChecklistItem(
        studyId,
        siteId,
        itemId,
        data,
      );
    } catch (error) {
      throw new Error(
        `Failed to update checklist item: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async submitChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.submitChecklist(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to submit checklist: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async createActivation(
    studyId: string,
    siteId: string,
    data: CreateActivationRequest,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.createActivation(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to create activation: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async addChecklistItem(
    studyId: string,
    siteId: string,
    data: AddChecklistItemRequest,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }
    if (!data.title?.trim()) {
      throw new Error("Checklist item title is required");
    }
    if (!data.category) {
      throw new Error("Checklist item category is required");
    }

    try {
      return await this.repository.addChecklistItem(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to add checklist item: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async removeChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
  ): Promise<{ message: string }> {
    if (!studyId || !siteId || !itemId) {
      throw new Error("Study ID, Site ID, and Item ID are required");
    }

    try {
      return await this.repository.removeChecklistItem(studyId, siteId, itemId);
    } catch (error) {
      throw new Error(
        `Failed to remove checklist item: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async submitChecklistDraft(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.submitChecklistDraft(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to submit checklist draft: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getSiteReadiness(
    studyId: string,
    siteId: string,
  ): Promise<SiteReadinessData> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getSiteReadiness(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch site readiness: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getDocumentReviews(
    studyId: string,
    siteId: string,
  ): Promise<DocumentReview[]> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getDocumentReviews(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch document reviews: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async uploadDocument(
    studyId: string,
    siteId: string,
    documentData: FormData,
  ): Promise<DocumentReview> {
    if (!studyId || !siteId || !documentData) {
      throw new Error("Study ID, Site ID, and Document data are required");
    }

    const fileEntry = documentData.get("file");
    if (fileEntry instanceof File) {
      assertUploadWithin10Mb(fileEntry);
    }

    try {
      return await this.repository.uploadDocument(
        studyId,
        siteId,
        documentData,
      );
    } catch (error) {
      const sizeMessage = uploadSizeLimitApiMessage(error);
      throw new Error(
        sizeMessage ?? getErrorMessage(error, "Failed to upload document"),
      );
    }
  }

  async approveDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<DocumentReview> {
    if (!studyId || !siteId || !documentId) {
      throw new Error("Study ID, Site ID, and Document ID are required");
    }

    try {
      return await this.repository.approveDocument(studyId, siteId, documentId);
    } catch (error) {
      throw new Error(
        `Failed to approve document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async rejectDocument(
    studyId: string,
    siteId: string,
    documentId: string,
    reason: string,
  ): Promise<DocumentReview> {
    if (!studyId || !siteId || !documentId || !reason) {
      throw new Error(
        "Study ID, Site ID, Document ID, and Reason are required",
      );
    }

    try {
      return await this.repository.rejectDocument(
        studyId,
        siteId,
        documentId,
        reason,
      );
    } catch (error) {
      throw new Error(
        `Failed to reject document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async deleteDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ message: string }> {
    if (!studyId || !siteId || !documentId) {
      throw new Error("Study ID, Site ID, and Document ID are required");
    }

    try {
      return await this.repository.deleteDocument(studyId, siteId, documentId);
    } catch (error) {
      throw new Error(
        `Failed to delete document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getDocumentDownloadUrl(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ url: string; fileName: string }> {
    if (!studyId || !siteId || !documentId) {
      throw new Error("Study ID, Site ID, and Document ID are required");
    }

    try {
      return await this.repository.getDocumentDownloadUrl(
        studyId,
        siteId,
        documentId,
      );
    } catch (error) {
      throw new Error(
        `Failed to get document download URL: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getActivationStatus(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch activation status: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // PM-specific workflow operations
  async startInitiation(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.startInitiation(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to start initiation: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async approveChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.approveChecklist(studyId, siteId, notes);
    } catch (error) {
      throw new Error(
        `Failed to approve checklist: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async rejectChecklist(
    studyId: string,
    siteId: string,
    reason: string,
  ): Promise<ActivationWorkflowStatus> {
    if (!studyId || !siteId || !reason) {
      throw new Error("Study ID, Site ID, and Reason are required");
    }

    try {
      return await this.repository.rejectChecklist(studyId, siteId, reason);
    } catch (error) {
      throw new Error(
        `Failed to reject checklist: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // Dashboard operations
  async getDashboard(filters?: DashboardFilters): Promise<DashboardResponse> {
    try {
      return await this.repository.getDashboard(filters);
    } catch (error) {
      throw new Error(
        `Failed to fetch dashboard: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getDashboardSummary(studyId?: string): Promise<DashboardSummary> {
    try {
      return await this.repository.getDashboardSummary(studyId);
    } catch (error) {
      throw new Error(
        `Failed to fetch dashboard summary: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // Export operations
  async exportToCSV(filters?: DashboardFilters): Promise<Blob> {
    try {
      return await this.repository.exportToCSV(filters);
    } catch (error) {
      throw new Error(
        `Failed to export CSV: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // Audit history
  async getAuditHistory(
    filters?: AuditHistoryFilters,
  ): Promise<AuditHistoryResponse> {
    try {
      return await this.repository.getAuditHistory(filters);
    } catch (error) {
      throw new Error(
        `Failed to fetch audit history: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  // Task operations
  async getTasks(
    studyId: string,
    siteId: string,
    filters?: TaskFilters,
  ): Promise<TasksResponse> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getTasks(studyId, siteId, filters);
    } catch (error) {
      throw new Error(
        `Failed to fetch tasks: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<SiteActivationTask> {
    if (!studyId || !siteId || !taskId) {
      throw new Error("Study ID, Site ID, and Task ID are required");
    }

    try {
      return await this.repository.getTask(studyId, siteId, taskId);
    } catch (error) {
      throw new Error(
        `Failed to fetch task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async createTask(
    studyId: string,
    siteId: string,
    data: CreateTaskRequest,
  ): Promise<SiteActivationTask> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    if (
      !data.taskTitle ||
      !data.description ||
      !data.assignedToId ||
      !data.dueDate ||
      !data.priority
    ) {
      throw new Error("All required task fields must be provided");
    }

    try {
      return await this.repository.createTask(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to create task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async updateTask(
    studyId: string,
    siteId: string,
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<SiteActivationTask> {
    if (!studyId || !siteId || !taskId) {
      throw new Error("Study ID, Site ID, and Task ID are required");
    }

    try {
      return await this.repository.updateTask(studyId, siteId, taskId, data);
    } catch (error) {
      throw new Error(
        `Failed to update task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async deleteTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<{ message: string }> {
    if (!studyId || !siteId || !taskId) {
      throw new Error("Study ID, Site ID, and Task ID are required");
    }

    try {
      return await this.repository.deleteTask(studyId, siteId, taskId);
    } catch (error) {
      throw new Error(
        `Failed to delete task: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getAssignableUsers(
    studyId: string,
    siteId: string,
  ): Promise<AssignableUser[]> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }

    try {
      return await this.repository.getAssignableUsers(studyId, siteId);
    } catch (error) {
      throw new Error(
        `Failed to fetch assignable users: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  }

  async getStudyChecklistConfig(
    studyId: string,
  ): Promise<StudyChecklistConfig> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.repository.getStudyChecklistConfig(studyId);
    } catch (error) {
      throw new Error(
        `Failed to fetch study checklist config: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async createStudyChecklistConfig(
    studyId: string,
    data: CreateStudyChecklistConfigRequest,
  ): Promise<StudyChecklistConfig> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.repository.createStudyChecklistConfig(studyId, data);
    } catch (error) {
      throw new Error(
        `Failed to create study checklist config: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async addStudyConfigItem(
    studyId: string,
    data: AddStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.repository.addStudyConfigItem(studyId, data);
    } catch (error) {
      throw new Error(
        `Failed to add study config item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async waiveGlobalItem(
    studyId: string,
    data: WaiveGlobalItemRequest,
  ): Promise<WaivedGlobalItem> {
    if (!studyId) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.repository.waiveGlobalItem(studyId, data);
    } catch (error) {
      throw new Error(
        `Failed to waive global item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async excludeSiteItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: ExcludeSiteItemRequest,
  ): Promise<ChecklistItem> {
    if (!studyId || !siteId || !itemId) {
      throw new Error("Study ID, Site ID, and Item ID are required");
    }
    try {
      return await this.repository.excludeSiteItem(
        studyId,
        siteId,
        itemId,
        data,
      );
    } catch (error) {
      throw new Error(
        `Failed to exclude checklist item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }

  async addSiteExceptionItem(
    studyId: string,
    siteId: string,
    data: AddSiteExceptionItemRequest,
  ): Promise<ChecklistItem> {
    if (!studyId || !siteId) {
      throw new Error("Study ID and Site ID are required");
    }
    try {
      return await this.repository.addSiteExceptionItem(studyId, siteId, data);
    } catch (error) {
      throw new Error(
        `Failed to add site exception item: ${getErrorMessage(error, "Unknown error")}`,
      );
    }
  }
}
